import React, { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';
import ModalLayout from '../../../../shared/components/modal/ModalLayout';
import { StHeader, StModalButtonLine } from '../../../../shared/components/modal/ConfirmationModalLayout';
import { CAT_MAN_PRODUCT_GROUP_SORTING } from '../../../../shared/common/SortByDropDown';
import { DropDownMenu } from '../../../../shared/common/DropDownMenu';
import MaterialIcon from '../../../../shared/common/MaterialIcon';
import styled from 'styled-components';
import { Button, ButtonSize, ButtonTheme } from '../../../../shared/common/Button';
import { StCancelButton } from '../../../../shop/reserve/components/ReserveListDetailPopup';
import { HelpTooltip } from '../../../../shared/common/HelpTooltip';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '../../../../shared/common/Checkbox';
import { THEME_ALU, THEME_RED, THEME_SMOKY, themeColor } from '../../../../styles/basics/colors';
import { LANGUAGES, ProductGroupTitle } from './mappers';
import { useSaveLibraryManagedProductGroupProperties } from '../api/useSaveLibraryManagedProductGroupProperties';
import { usePaginationAndSortParams } from '../../../../shared/hooks/usePaginationAndSortParams';

interface Props {
    title: string;
    hideModal: () => void;
    sorting: SortingItem;
    orderedTitles: ProductGroupTitle[];
    productGroupId: string;
}

export type SortingItem = {
    value: string;
    label: string;
};

const StContentWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;
    justify-content: space-between;
    margin-top: 32px;
    font-size: 13px !important;
`;

const StValidationError = styled.span`
    color: ${themeColor(THEME_RED)};
    font-size: 12px;
`;

const StInputFieldWrapper = styled.div`
    width: 100%;
    margin-bottom: 26px;
    & input {
        width: 100% !important;
        border-bottom: 1px solid ${themeColor(THEME_ALU)};
    }
`;

const StLabel = styled.label<{ required: boolean }>`
    color: ${themeColor(THEME_ALU)};
    &:after {
        content: ${props => (props.required ? '"*"' : '""')};
        color: ${themeColor(THEME_RED)};
        margin-left: 3px;
    }
`;

const ListItem = styled.li`
    position: relative;
    min-width: 200px;
    cursor: pointer;
    transition-duration: 0.2s;

    &:hover {
        color: #7fc31c;
    }

    padding: 2px 10px;

    .material-icon {
        top: 3px;
        right: 5px;
        transform: none;
    }
`;

const SortingLabel = styled.div`
    font-size: 15px !important;
    margin-left: 5px;
`;

const SortingWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    justify-content: flex-start;
`;

const StSeparator = styled.div`
    border-bottom: 1px solid ${themeColor(THEME_SMOKY)};
    margin: 34px 0;
`;

const EditPropertiesModal: FC<Props> = ({ title, hideModal, sorting, orderedTitles, productGroupId }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const [isEnglishTitleValid, setIsEnglishTitleValid] = useState(true);

    const [isTitlesEnabled, setIsTitlesEnabled] = useState(false);
    const handleEnableTitles = () => {
        setIsTitlesEnabled(!isTitlesEnabled);
    };

    const [titles, setTitles] = useState<ProductGroupTitle[]>(orderedTitles);

    const handleSetTitle = (e: ChangeEvent<HTMLInputElement>, language: string) => {
        const title = e.target.value;

        setTitles(prevTitles => {
            return prevTitles.map(item => {
                if (item.language === language) {
                    return { language, title };
                }
                return item;
            });
        });
    };
    const { setSortBy } = usePaginationAndSortParams();

    const [productGroupSorting, setProductGroupSorting] = useState<SortingItem>({
        label: sorting.label,
        value: sorting.value
    });

    const { mutate: onSaveProperties, isLoading: isSavingProperties } =
        useSaveLibraryManagedProductGroupProperties(productGroupId);

    const filteredTitles = titles.filter(item => !!item.title.trim());

    const resetTitles = titles.filter(item => item.language === LANGUAGES.en);

    function handleSaveProductGroupProperties() {
        onSaveProperties(
            {
                titles: isTitlesEnabled ? filteredTitles : resetTitles,
                sorting: productGroupSorting.value
            },
            {
                onSuccess: (_, { sorting }) => {
                    hideModal();
                    setSortBy(sorting === 'custom' ? 'custom' : 'releaseDateNewToOld');
                }
            }
        );
    }

    useEffect(() => {
        const hasMultiLanguageTitles = orderedTitles
            .filter(item => item.language !== LANGUAGES.en)
            .some(item => !!item.title.trim());

        setIsTitlesEnabled(hasMultiLanguageTitles);
        setTitles(orderedTitles);
    }, [orderedTitles]);

    useEffect(() => {
        setProductGroupSorting({
            value: sorting.value,
            label: sorting.label
        });
    }, [sorting.value, sorting.label]);

    const handleSortChange = useCallback((item: SortingItem) => {
        setProductGroupSorting(item);
        setExpanded(false);
    }, []);

    return (
        <ModalLayout
            header={
                <StHeader>
                    {title}
                    <StSeparator />
                </StHeader>
            }
            content={
                <>
                    <SortingWrapper>
                        <HelpTooltip size="large" className="ml-sm">
                            <p>{t('marketing.productGroups.sortTooltip')}</p>
                        </HelpTooltip>
                        <DropDownMenu
                            label={
                                <SortingLabel>
                                    <span>{t('marketing.productGroups.sortBy')}&nbsp;</span>
                                    <b>
                                        {t(`components.catManProductGroupSorting.${productGroupSorting.value}`, {
                                            defaultValue: productGroupSorting.label
                                        })}
                                    </b>
                                </SortingLabel>
                            }
                            expanded={expanded}
                            align="left"
                            onToggleClick={() => setExpanded(!expanded)}
                            onClickOutside={() => setExpanded(false)}
                            className="age-category-selector"
                        >
                            {CAT_MAN_PRODUCT_GROUP_SORTING.map(item => {
                                const isActive = item.value === productGroupSorting.value;
                                return (
                                    <ListItem
                                        className="age-category-selector-item"
                                        key={item.value}
                                        onClick={() => handleSortChange(item)}
                                    >
                                        <span>
                                            {t(`components.catManProductGroupSorting.${item.value}`, {
                                                defaultValue: item.label
                                            })}
                                        </span>
                                        {isActive ? <MaterialIcon icon="check" primary={true} /> : null}
                                    </ListItem>
                                );
                            })}
                        </DropDownMenu>
                    </SortingWrapper>
                    <StContentWrapper>
                        <StInputFieldWrapper>
                            <Checkbox
                                onChange={handleEnableTitles}
                                checked={isTitlesEnabled}
                                label={t('marketing.productGroups.productGroupTitles.checkboxLabel')}
                            />
                        </StInputFieldWrapper>

                        {isTitlesEnabled
                            ? titles.map(item => {
                                  return (
                                      <StInputFieldWrapper key={item.language}>
                                          <StLabel required={item.language === LANGUAGES.en}>
                                              {t(`marketing.productGroups.productGroupTitles.labels.${item.language}`)}
                                          </StLabel>
                                          <input
                                              onBlur={() => {
                                                  if (item.language === LANGUAGES.en && !item.title.trim()) {
                                                      setIsEnglishTitleValid(false);
                                                  } else {
                                                      setIsEnglishTitleValid(true);
                                                  }
                                              }}
                                              onChange={e => handleSetTitle(e, item.language)}
                                              name={item.language}
                                              defaultValue={item.title}
                                          />
                                          {item.language === LANGUAGES.en && !isEnglishTitleValid ? (
                                              <StValidationError>
                                                  {t('marketing.productGroups.productGroupTitles.requiredError')}
                                              </StValidationError>
                                          ) : null}
                                      </StInputFieldWrapper>
                                  );
                              })
                            : null}
                    </StContentWrapper>
                </>
            }
            footer={
                <StModalButtonLine>
                    <StCancelButton
                        size={ButtonSize.LARGE}
                        theme={ButtonTheme.POSITIVE_INVERT}
                        text={t('cancel')}
                        onClick={hideModal}
                        disabled={isSavingProperties}
                    />
                    <Button
                        text={t('save')}
                        isLoading={isSavingProperties}
                        disabled={!isEnglishTitleValid || isSavingProperties}
                        onClick={handleSaveProductGroupProperties}
                        data-displayname="ConfirmationButton"
                        size={ButtonSize.LARGE}
                    />
                </StModalButtonLine>
            }
            visible={true}
            width={530}
            closable={false}
            closeOnEsc={false}
        />
    );
};

export default EditPropertiesModal;
